// ============================================================================
// Editing a project's service lines. Run C Phase 2, 2026-08-19.
//
// The AM entry screen and the project detail page now edit the SAME shape with
// the SAME component. These are the two adapters between the persisted row and
// the string-bound form shape, plus the diff that decides which lines are
// actually written on save.
//
// Why a diff at all: `saveProjectServiceLine` is a patch, and the cheapest way
// to keep a save honest is to send nothing for a line nobody touched. A project
// with three lines where one changed produces one UPDATE, not three — so an
// unrelated line cannot be collaterally rewritten by a save it was not part of.
// ============================================================================
import type { ProjectServiceLine } from '../../../shared/types';
import type { ProjectServiceLineInput } from '../hooks/use-project-data';
import { blankLine, type EntryLine } from './entry-derive';

const str = (v: string | number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

/** A persisted line, as the editor's string-bound shape. */
export function serviceLineToEntryLine(row: ProjectServiceLine, key: string): EntryLine {
  return {
    ...blankLine(key),
    service_line_id: row.service_line_id,
    line_role: row.line_role === 'chargeback' ? 'chargeback' : 'service',
    service_code: row.service_code ?? '',
    division_id: row.service_catalog?.division_id ?? '',
    scope_text: str(row.scope_text),
    scope_qty: str(row.scope_qty),
    assigned_partner_id: str(row.assigned_partner_id),
    service_date_planned: str(row.service_date_planned),
    service_date_actual: str(row.service_date_actual),
    client_rate: str(row.client_rate),
    sub_rate: str(row.sub_rate),
  };
}

const numOrNull = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/**
 * The form shape as a write payload.
 *
 * `notes` and `scope_unit` are deliberately ABSENT, not null: the editor has no
 * input for either, and `saveProjectServiceLine` no longer writes a key it was
 * not given. Sending `notes: null` here is what used to erase a line's notes on
 * every unrelated save.
 */
export function entryLineToInput(l: EntryLine, lineOrder: number): ProjectServiceLineInput {
  return {
    line_role: l.line_role,
    // A chargeback carries no service code, and the DB constraint enforces it.
    service_code: l.line_role === 'chargeback' ? null : l.service_code,
    scope_text: l.scope_text.trim() || null,
    scope_qty: numOrNull(l.scope_qty),
    client_rate: numOrNull(l.client_rate),
    sub_rate: numOrNull(l.sub_rate),
    assigned_partner_id: l.assigned_partner_id || null,
    service_date_planned: l.service_date_planned || null,
    service_date_actual: l.service_date_actual || null,
    line_order: lineOrder,
  };
}

/** Every field the editor can change. `key` is React's, never the database's. */
const EDITABLE: Array<keyof EntryLine> = [
  'line_role',
  'service_code',
  'division_id',
  'scope_text',
  'scope_qty',
  'assigned_partner_id',
  'service_date_planned',
  'service_date_actual',
  'client_rate',
  'sub_rate',
];

export function lineChanged(before: EntryLine | undefined, after: EntryLine): boolean {
  if (!before) return true;
  return EDITABLE.some((k) => before[k] !== after[k]);
}

export interface LineDiff {
  /** Lines with no id yet — inserted. */
  added: EntryLine[];
  /** Persisted lines whose editable fields changed — updated, as a patch. */
  changed: EntryLine[];
  /** Persisted lines whose position in the list moved. */
  reordered: EntryLine[];
  /** True when anything at all needs writing. */
  dirty: boolean;
}

/**
 * What a save actually has to write.
 *
 * `original` is the list as loaded; `current` is the list as edited. Removal is
 * NOT part of this diff — a line is removed through the sanctioned delete RPC at
 * the moment the person confirms it, not accumulated and flushed on save, so a
 * deletion can never ride along inside an unrelated Save click.
 */
export function diffLines(original: EntryLine[], current: EntryLine[]): LineDiff {
  const byId = new Map(original.filter((l) => l.service_line_id).map((l) => [l.service_line_id, l]));
  const added: EntryLine[] = [];
  const changed: EntryLine[] = [];
  const reordered: EntryLine[] = [];

  current.forEach((l, i) => {
    if (!l.service_line_id) {
      added.push(l);
      return;
    }
    const before = byId.get(l.service_line_id);
    if (lineChanged(before, l)) {
      changed.push(l);
      return;
    }
    const wasAt = original.findIndex((o) => o.service_line_id === l.service_line_id);
    if (wasAt !== i) reordered.push(l);
  });

  return {
    added,
    changed,
    reordered,
    dirty: added.length > 0 || changed.length > 0 || reordered.length > 0,
  };
}
